/**
 * In-session approval resolve client (Conductor spec 03 §runtime-gate).
 *
 * Wraps the per-session HITL approval endpoints in the gateway:
 *   GET  /api/sessions/:sid/approval/pending            → { pending: [...] }
 *   POST /api/sessions/:sid/approval/:eventId/resolve   → { ok, decision }
 *   POST /api/sessions/:sid/approval/:eventId/veto      → { ok, decision }
 *
 * The pending rows come back snake_case straight off the gateway (raw DB rows),
 * with `tool_input` already redacted to a safe preview — `listPending` maps each
 * row to a camelCase `ApprovalEvent` so the UI never touches snake_case keys.
 *
 * `resolve` settles a paused gate (approve / deny); it is first-writer-wins on
 * the open→settled transition, so the caller surfaces "already settled" on a 409.
 * `veto` cancels a HOTL hold-and-proceed before its timer fires (409
 * `already_proceeded` if the action already went through).
 */
import { apiJson } from './client';

// ── Read shape ──────────────────────────────────────────────────────
/** One pending approval gate, mapped from the gateway's raw snake_case row. */
export interface ApprovalEvent {
  id: string;
  orgId: string;
  cardId: string | null;
  action: string;
  toolName: string | null;
  toolInput: unknown;
  autonomyMode: string;
  decision: string;
  matchedRuleId: string | null;
  createdAt: string;
}

/** Raw snake_case row shape as returned by the gateway pending endpoint. */
interface ApprovalEventRow {
  id: string;
  org_id: string;
  card_id: string | null;
  action: string;
  tool_name: string | null;
  tool_input: unknown;
  autonomy_mode: string;
  decision: string;
  matched_rule_id: string | null;
  created_at: string;
}

function toApprovalEvent(row: ApprovalEventRow): ApprovalEvent {
  return {
    id: row.id,
    orgId: row.org_id,
    cardId: row.card_id,
    action: row.action,
    toolName: row.tool_name,
    toolInput: row.tool_input,
    autonomyMode: row.autonomy_mode,
    decision: row.decision,
    matchedRuleId: row.matched_rule_id,
    createdAt: row.created_at,
  };
}

export const approvalsApi = {
  /** Pending gates for one session, mapped to camelCase ApprovalEvent rows. */
  listPending(sessionId: string): Promise<{ pending: ApprovalEvent[] }> {
    return apiJson<{ pending: ApprovalEventRow[] }>(
      `/sessions/${sessionId}/approval/pending`,
    ).then((r) => ({ pending: r.pending.map(toApprovalEvent) }));
  },
  /** Settle a paused gate. 409 if it was already settled by another writer. */
  resolve(
    sessionId: string,
    eventId: string,
    decision: 'approve' | 'deny',
    principalFence: string,
  ): Promise<{ ok: true; decision: string }> {
    return apiJson<{ ok: true; decision: string }>(
      `/sessions/${sessionId}/approval/${eventId}/resolve`,
      {
        method: 'POST',
        json: { decision },
        headers: { 'X-Chaos-Expected-Principal': principalFence },
      },
    );
  },
  /** Cancel a HOTL hold-and-proceed. 409 `already_proceeded` if it already ran. */
  veto(
    sessionId: string,
    eventId: string,
    principalFence: string,
  ): Promise<{ ok: true; decision: string }> {
    return apiJson<{ ok: true; decision: string }>(
      `/sessions/${sessionId}/approval/${eventId}/veto`,
      {
        method: 'POST',
        json: {},
        headers: { 'X-Chaos-Expected-Principal': principalFence },
      },
    );
  },
};

// ── Query keys ──────────────────────────────────────────────────────
export const approvalKeys = {
  all: ['approvals'] as const,
  pending: (sessionId: string) => [...approvalKeys.all, 'pending', sessionId] as const,
};
